import { isFunction, isUndefinedOrNull, safeRound } from "./utils";

/**
 * @module
 * Various utilities/helpers for array objects
 */

/**
 * Returns true if "a" is undefined, null, or empty
 * @param a Array
 * @returns true if undefined/null/empty, false otherwise
 */
export function isArrayEmpty<T>(a: Array<T>): boolean {
    return !(a && a.length > 0);
}

/**
 * Groups the items of an array using a custom key selector: ```T[] -> { key: TKey, values: T[] }[]```
 *
 * @param items Array
 * @param keySelector Selects the property to group by on each element of the source array
 * @param keyHash Selects the property to use for equality checks on elements returned by "keySelector"
 * @returns Grouped items, as { key: TKey, values: T[] }[]
 *
 * @example
 * ```
 * const array = [
 *      { group: "g1", value: 1.1 },
 *      { group: "g1", value: 1.2 },
 *      { group: "g2": value: 2 }
 * ]
 *
 * const groups = groupBy(array, g => g.group)
 *
 *
 * groups = [
 *      { key: "g1", values: [ { group: "g1", value: 1.1 }, { group: "g1", value: 1.2 } ] },
 *      { key: "g2", values: [ { group: "g2", value: 2 } ] }
 * ]
 * ```
 */
export function groupBy<T, TKey>(
    items: T[],
    keySelector: (item: T, index: number) => TKey,
    keyHash?: (key: TKey) => any
): { key: TKey; values: T[] }[] {
    if (!isFunction(keyHash)) keyHash = (key: TKey) => key;

    let groups = {};
    let index = 0;

    items.forEach((i) => {
        var key = keySelector(i, index++);
        var hash = keyHash(key);

        var group = groups[hash];

        if (!group) {
            group = { key: key, values: [] };
            groups[hash] = group;
        }

        group.values.push(i);
    });

    return Object.keys(groups).map((k) => groups[k]);
}

/**
 * @deprecated In retrospect, this is useless, and it should be equivalent to "groupBy" without the "keyHash" argument
 */
export function groupByEquality<T, TKey>(items: T[], keySelector: (item: T) => TKey) {
    let groups: {
        key: TKey;
        items: T[];
    }[] = [];

    groups = items.reduce((groups, l) => {
        const key = keySelector(l);
        let target = groups.find((g) => g.key === key);

        if (!target) {
            target = { key: key, items: [] };
            groups.push(target);
        }

        target.items.push(l);

        return groups;
    }, groups);

    return groups;
}

/**
 * Returns distinct elements from an array
 * @param items
 * @param valueSelector Selects the property to group by on each element of the source array
 * @param valueHash Selects the property to use for equality checks on elements returned by "valueSelector"
 * @returns Distinct results
 * @remarks
 * This is the same as calling "groupBy", and retaining only the key
 * It could be easier to create a Set object, e.g., const set = new Set(array.map(a => valueSelector(a)))
 */
export function getDistinctValues<T, TKey>(
    items: T[],
    valueSelector: (item: T) => TKey,
    valueHash?: (key: TKey) => any
): TKey[] {
    var groups = groupBy(items, valueSelector, valueHash);

    return groups.map((g) => g.key);
}

/**
 * Flattens a an array of arrays
 * @param items Array of arrays
 * @returns Flat array
 * @remarks We should probably consider moving to ES2019 in tsconfig.json file to be able to use Array.prototype.flatMap()
 */
export function selectMany<T>(items: T[][]): T[] {
    if (items) {
        return Array.prototype.concat.apply([], items);
    }
}

/**
 * Creates a range/sequence of consecutive numbers
 * @param start Starting number
 * @param length Number of elements
 * @param selector Selector applied to each number
 * @returns Sequence of numbers
 * @example
 *
 * const range = createRange(1, 10, i => "n" + i)
 *
 * // range: ["n1", "n2", ..., "n10"]
 */
export function createRange<T>(start: number, length: number, selector: (index: number) => T): T[] {
    return Array.apply(null, Array(length))
        .map((i, index) => start + index)
        .map((index) => selector(index));
}

/**
 * Computes the sum of all elements in an array
 * @param items Array
 * @param valueSelector Value to select from each element in the array
 * @returns Sum
 * @example
 *
 * const array = [1, 2, 3]
 * const s = sum(array, v => v)
 * // s: 6
 */
export function sum<T>(items: T[], valueSelector: (item: T) => number): number {
    if (items.length === 1) {
        return valueSelector(items[0]);
    } else if (items.length > 1) {
        return items.map((s) => valueSelector(s)).reduce((a, b) => a + b);
    }

    return 0;
}

/**
 * Return the minimum value in an array (according to the < operator)
 * @param items Array
 * @param valueSelector Value selector applied to the array's elements
 * @returns Minimum value
 * @remarks Returns null if the array is empty (old C# habit), and does not check if the array is defined
 */
export function arrayMin<T, TKey>(items: T[], valueSelector: (item: T) => TKey): TKey {
    if (items.length > 0) {
        if (items.length === 1) {
            return valueSelector(items[0]);
        } else if (items.length > 1) {
            return items.map((s) => valueSelector(s)).reduce((p, v) => (p < v ? p : v));
        }
    }

    return null;
}

/**
 * Return the maximum value in an array (according to the > operator)
 * @param items Array
 * @param valueSelector Value selector applied to the array's elements
 * @returns Maximum value
 * @remarks Returns null if the array is empty (old C# habit), and does not check if the array is defined
 */
export function arrayMax<T, TKey>(items: T[], valueSelector: (item: T) => TKey): TKey {
    if (items.length > 0) {
        if (items.length === 1) {
            return valueSelector(items[0]);
        } else if (items.length > 1) {
            return items.map((s) => valueSelector(s)).reduce((p, v) => (p > v ? p : v));
        }
    }

    return null;
}

/**
 * Computes the average (mean) of all elements in an array
 * @param items Array
 * @param valueSelector Value selector applied to the array's elements
 * @returns Average
 */
export function avg<T>(items: T[], valueSelector: (item: T) => number): number {
    const s = sum(items, valueSelector);
    const count = items.length || 1;

    return s / count;
}

export function createLookup<T, K extends string | number>(items: T[], keySelector: (item: T) => K): Record<K, T[]>
export function createLookup<T, K extends string | number, V>(items: T[], keySelector: (item: T) => K, valueSelector: (item: T) => V): Record<K, V[]>
export function createLookup<T, K extends string | number, V>(items: T[], keySelector: (item: T) => K, valueSelector?: (item: T) => V): Record<K, T[]> {

    let index = {} as Record<K, any>;

    const groups = groupBy(items, keySelector);

    groups.forEach((g) => {
        index[g.key] = valueSelector
            ? g.values.map(valueSelector)
            : g.values;
    });

    return index;
}

export function mergeLists<T>(targets: T[], sources: T[], keySelector: (item: T) => number | string): T[] {
    targets = targets || [];

    let index = targets.reduce((index, item) => {
        index[keySelector(item)] = item;
        return index;
    }, {});

    sources.forEach((s) => (index[keySelector(s)] = s));

    return Object.keys(index).map((k) => index[k]);
}

/**
 * Creates a range/sequence of "n" numbers, starting from 0
 * @param n Number of numbers
 * @remarks Very similar to "createRange", should probably merge
 */
export function range(n: number, start: number = 0) {
    const array = [];
    for (let i = 0; i < n; i++) {
        array.push(i + start);
    }
    return array;
}

/**
 * Transforms an array into an object, using "keySelector" to provide the key of each item in the array
 * The resulting object can be used as an index
 * @param items Array
 * @param keySelector Key selector applied to each item
 * @returns Resulting object
 * @remarks If multiple items have the same key, the latest will be retained
 */
export function createIndex<T, K extends string | number>(items: T[], keySelector: (item: T) => K): Record<K, T> {
    const index = {} as Record<K, T>;

    if (items) {
        items.forEach((i) => {
            const key = keySelector(i);
            index[key] = i;
        });
    }

    return index;
}

/**
 * Partitions (splits) an array in 2 arrays, according to a predicate
 * @param items Array
 * @param predicate Predicate applied to each element
 * @returns An tuple with 2 elements, the first being all items for which the predicate was true, the 2nd being the others
 * @example
 *
 * const a = [1, 2, 3, 4, 5]
 * const [x, y] = partition(a, i => i < 3)
 * // x: [1, 2]
 * // y: [3, 4, 5]
 */
export function partition<T>(items: T[], predicate: (item: T) => boolean): [T[], T[]] {
    const state: [T[], T[]] = [[], []];

    const res = items.reduce((result, i) => {
        const p = result[predicate(i) ? 0 : 1];
        p.push(i);
        return result;
    }, state);

    return res;
}

/**
 * Returns all the elements of the firsr ("all") array, except those found in the 2nd ("except"),
 * using strict inequality (!==)
 * @param all First array
 * @param except Second array
 * @returns "all" - "except"
 */
export function except<T>(all: T[], except: T[]) {
    return all.filter((a) => except.every((r) => r !== a));
}

export function toggle<T>(array: T[], value: T, toggle: boolean) {
    return [...new Set(toggle ? [...array, value] : array.filter((x) => x !== value))];
}

/**
 * Converts an array into a Map object, using a key selector argument
 * @param items Array
 * @param keySelector Key selector
 * @returns Map
 */
export function createMap<V, K = number | string>(items: V[], keySelector: (item: V) => K): Map<K, V> {
    return new Map((items || []).map((i) => [keySelector(i), i])) as Map<K, V>;
}

/**
 * Recursively flattens the elements of an array
 * @param items Array
 * @param selector Selector applied to each element. If the result is an array, "flatten" is called recursively.
 * @returns Flat array
 */
export function flatten<T>(items: T[], selector: (i: T) => T[]): T[] {
    return isArrayEmpty(items) ? [] : selectMany(items.map((t) => [t].concat(flatten(selector(t), selector))));
}

/**
 * Compares the contents of 2 arrays
 * @param a First array
 * @param b Second array
 * @param sameOrder If true, elements have to be in the same order
 * @returns True if both array contain the same elements
 */
export function compareArrays<T>(a: T[], b: T[], sameOrder = false) {
    if (isArrayEmpty(a)) {
        return isArrayEmpty(b);
    }

    if (isArrayEmpty(b)) {
        return false;
    }

    if (a.length !== b.length) {
        return false;
    }

    if (sameOrder) {
        return a.every((ai, index) => b[index] === ai);
    }

    const set = new Set(a);
    return b.every((i) => set.has(i));
}

/**
 * Returns the first elements of an array matching a predicate
 * @param items Array
 * @param predicate Predicate
 * @returns All the elements of "items", up and not including the first for which "predicate" is false
 */
export function takeWhile<T>(items: T[], predicate: (a: T) => boolean) {
    const r = [];
    let index = 0;

    while (index < items.length) {
        const i = items[index++];
        if (!predicate(i)) break;
        r.push(i);
    }

    return r;
}

export type Sort<T> = (keyof T | ((item: T) => any)) | [keyof T | ((item: T) => any), "asc" | "desc"];

/**
 * Return a copy of the collection, sorted by the specified keys/value-accessors.
 * @param items A collection of items to sort.
 * @param sorts An array of keys or value-accessors to sort by, optionally including the sort direction "asc"/"desc".
 */
export function sortBy<T extends { [key: string]: any }>(items: T[], sorts: Sort<T>[]) {
    function withDirection(value: number, direction: "asc" | "desc") {
        // If we're sorting "desc", flip the value.
        return direction === "asc" ? value : value * -1;
    }

    return [...items].sort((a, b) => {
        for (const sort of sorts) {
            let direction: "asc" | "desc" = "asc";

            let aValue: T[keyof T];
            let bValue: T[keyof T];

            if (typeof sort === "function") {
                aValue = sort(a);
                bValue = sort(b);
            } else if (Array.isArray(sort)) {
                direction = sort[1] ?? direction;

                const accessor = sort[0];
                if (typeof accessor === "function") {
                    aValue = accessor(a);
                    bValue = accessor(b);
                } else {
                    aValue = a[accessor];
                    bValue = b[accessor];
                }
            } else {
                aValue = a[sort];
                bValue = b[sort];
            }

            // If they're both undefined, then they're equal.
            if (isUndefinedOrNull(aValue) && isUndefinedOrNull(bValue)) {
                continue;
            } else if (aValue === undefined) {
                return withDirection(1, direction);
            } else if (bValue === undefined) {
                return withDirection(-1, direction);
            }

            // If the values are both strings, use the locale-friendly comparison function.
            if (typeof aValue === "string" && typeof bValue === "string" && aValue.localeCompare !== undefined) {
                const result = aValue.localeCompare(bValue);
                if (result === 0) {
                    continue;
                }
                return withDirection(result, direction);
            }

            if (aValue > bValue) {
                return withDirection(1, direction);
            } else if (bValue > aValue) {
                return withDirection(-1, direction);
            }
        }

        return 0;
    });
}

// NOTE: function rebalanceAmounts has a copy version in Omnia\Shared\Omnia.Shared\Rebalancer.cs  - keep them synced if changes are made
/**
 * Rebalances amounts, adding difference to get the total, proportionally applying the difference based on weights. Note: when target is equal the sum of amounts, no changes will be done.
 * @param amounts The original amounts (e.g. product amount per ha).
 * @param target The target amount all amounts should sum up to.
 * @param weights The weights for the amounts (e.g. cropping area). Must be same lenght as amounts array and sum of weights cannot be 0.
 * @param amountsDp Decimal points precision of the amounts (int).
 * @param targetDp Decimal points precition of the target (int).
 * @returns New array of amounts rebalanced so their sum is equal the target. The amounts are rounded to amountsDp decimal places at the end to remove any floating point residue left by division.
 */
export function rebalanceAmounts(
    amounts: number[],
    target: number,
    weights: number[],
    amountsDp: number,
    targetDp: number,
): number[] {
    if (weights.length !== amounts.length) {
        throw new Error("weights and amounts must be the same length")
    }

    const powAmt = 10 ** amountsDp
    const powTgt = 10 ** targetDp
    const upScale = 10 ** (amountsDp - targetDp)

    const intAmounts = amounts.map(a => Math.round(a * powAmt))
    const targetInt = Math.round(target * powTgt) * upScale

    const currentSum = intAmounts.reduce((s, x) => s + x, 0)
    let diff = targetInt - currentSum

    if (diff === 0) {
        // already balanced - return a copy of the array
        return amounts.slice()
    }

    const totalWeight = weights.reduce((s, w) => s + w, 0)
    if (totalWeight === 0) {
        throw new Error("sum of weights cannot be zero")
    }

    const sharesInt: number[] = []
    const remainders: number[] = []
    weights.forEach((w, i) => {
        const exact = diff * w / totalWeight
        const integer = exact < 0 ? Math.ceil(exact) : Math.floor(exact) // truncate toward 0
        sharesInt.push(integer)
        remainders.push(exact - integer) // leftover fraction
    })

    // units still to distribute because of truncation
    let leftover = diff - sharesInt.reduce((s, x) => s + x, 0)
    if (leftover !== 0) {
        // sort by the largest positive (or negative) reminder, to assign units to the most 'deserving' items first
        const order = [...weights.keys()].sort((i, j) => 
            diff > 0 ? remainders[j] - remainders[i]
                     : remainders[i] - remainders[j]
        )

        // loop through the ordered items, until leftover is zero and add/subtract units
        for (let k = 0; leftover !== 0; k = (k + 1) % order.length) {
            sharesInt[order[k]] += diff > 0 ? 1 : -1;
            leftover += diff > 0 ? -1 : 1;
        }
    }

    const newIntAmounts = intAmounts.map((a, i) => a + sharesInt[i])
    return newIntAmounts.map(a => safeRound(a / powAmt, amountsDp))
}
